import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  ArrowRightLeft,
  RefreshCw,
  Loader2,
  ChevronDown,
  ChevronRight,
  Filter,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { DashboardLayout } from '../components/DashboardLayout';
import { PersonalLayout } from '../components/PersonalLayout';
import { CurrencyToggle } from '../components/CurrencyToggle';
import { IntercompanySettlementControl } from '../components/IntercompanySettlementControl';
import { useEntityStore, usePortfolioPrefsStore } from '../store';
import { intercompanyAPI } from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { cn } from '../lib/utils';
import { buildEntriesView, buildNettedPositions, buildPositionBreakdown, convertLedgerEntry, ledgerCounterparties } from '../lib/intercompanyLedger';
import { format } from 'date-fns';

var SOURCE_LABELS = {
  gmail: 'Email',
  chief_of_staff: 'Chief of Staff',
  bank_transaction: 'Bank',
  auto_settlement: 'Auto Settlement',
  pennylane: 'Pennylane',
  manual: 'Manual',
  derived: 'Derived',
  derived_transaction: 'Bank',
};
function sourceLabel(s) { return SOURCE_LABELS[s] || s; }

var formatSignedCurrency = function formatSignedCurrency(value, currency) {
  if (currency === undefined) currency = 'GBP';
  var symbols = { GBP: '\u00A3', EUR: '\u20AC' };
  var prefix = value > 0 ? '+' : value < 0 ? '-' : '';
  return prefix + (symbols[currency] || currency) + Math.abs(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Plain-words direction (Fan, #finance-agent 2026-10-30: a "+" next to two
// names read as the wrong way round). Signs are from the party's side:
// positive = the counterparty owes the party (the party paid or lent).
function formatUnsigned(value, currency) {
  return formatSignedCurrency(Math.abs(value), currency).replace(/^\+/, '');
}
function owesSentence(partyName, counterpartyName, value, currency) {
  if (!value) return 'Nothing owed';
  var debtor = value > 0 ? counterpartyName : partyName;
  var creditor = value > 0 ? partyName : counterpartyName;
  return debtor + ' owes ' + creditor + ' ' + formatUnsigned(value, currency);
}

var INTERCOMPANY_TAB_DEFS = [
  { key: 'positions', label: 'Positions' },
  { key: 'entries', label: 'Entries' },
  { key: 'recharges', label: 'Cost recharges' },
];

// Tab state rides ?tab= (like ProfitLossPage's section tabs) so the same bar
// works in both Personal and Entities scope. The legacy /personal/intercompany/
// entries route stays valid as a fallback for existing links/bookmarks.
function resolveIntercompanyTab(searchParams, pathname) {
  var t = searchParams.get('tab');
  if (t === 'entries' || t === 'positions' || t === 'recharges') return t;
  if (pathname && pathname.endsWith('/entries')) return 'entries';
  return 'positions';
}

// Standard segmented tab control (Profit & Loss / Revenue / Costs pattern),
// shared across the app. Replaces the previous bespoke pill toggle.
function IntercompanyTabBar({ activeTab, onTabChange }) {
  return (
    <div
      className="inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground w-fit"
      data-testid="intercompany-tabs"
    >
      {INTERCOMPANY_TAB_DEFS.map(function (tab) {
        var isActive = tab.key === activeTab;
        return (
          <button
            key={tab.key}
            onClick={function () { onTabChange(tab.key); }}
            className={cn(
              'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              isActive && 'bg-background text-foreground shadow',
            )}
            data-testid={'intercompany-tab-' + tab.key}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}


// Drill-down for one month of one relationship: the actual entries behind the
// figure (Fan, #finance-agent 2026-08-30: "I still don't believe it because
// there is no detail... do a pop up when I click on the detail to understand
// what transactions are considered in there"). These are the SAME ledger
// entries the Entries tab renders and the same ones summed into the month's
// movement -- rendered, not re-derived, so the list can never disagree with
// the number that opened it.
function EntryDetailDialog({ detail, fxRates, displayCurrency, onClose }) {
  if (!detail) return null;
  var entries = (detail.entries || []).slice().sort(function (a, b) {
    return String(b.date || '') < String(a.date || '') ? -1 : 1;
  });
  var convertedTotal = 0;
  var unconvertible = 0;
  entries.forEach(function (e) {
    var c = convertLedgerEntry(e, displayCurrency, fxRates);
    if (c.unconvertible) unconvertible += 1;
    else convertedTotal += c.value || 0;
  });

  return (
    <Dialog open={!!detail} onOpenChange={function (open) { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="intercompany-entry-detail">
        <DialogHeader>
          <DialogTitle>
            {detail.counterpartyName + ' \u00B7 ' + detail.label}
          </DialogTitle>
          <DialogDescription>
            {detail.relationshipLabel
              ? owesSentence(detail.relationshipLabel, detail.counterpartyName, convertedTotal, displayCurrency) +
                ' (' + entries.length + ' entr' + (entries.length === 1 ? 'y' : 'ies') + ')'
              : entries.length + ' entr' + (entries.length === 1 ? 'y' : 'ies') +
                ' making up ' + formatSignedCurrency(convertedTotal, displayCurrency)}
            {unconvertible > 0 ? ' (' + unconvertible + ' unconvertible, excluded)' : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-background">
              <tr className="border-b text-muted-foreground">
                <th className="text-left py-1 pr-3">Date</th>
                <th className="text-left py-1 pr-3">Description</th>
                <th className="text-left py-1 pr-3">Reference</th>
                <th className="text-left py-1 pr-3">Source</th>
                <th className="text-left py-1 pr-3">Paid by</th>
                <th className="text-right py-1 pr-3">Native</th>
                <th className="text-right py-1 pr-3">{displayCurrency}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(function (e, i) {
                var c = convertLedgerEntry(e, displayCurrency, fxRates);
                var native = Number(e.signed_amount);
                return (
                  <tr
                    key={(e.entry_id || 'entry') + '-' + i}
                    className="border-b border-border/50 align-top"
                    data-testid={'entry-detail-row-' + (e.entry_id || i)}
                  >
                    <td className="py-1 pr-3 whitespace-nowrap">{e.date || '--'}</td>
                    <td className="py-1 pr-3">{e.description || '--'}</td>
                    <td className="py-1 pr-3 text-muted-foreground">{e.reference || '--'}</td>
                    <td className="py-1 pr-3">
                      <Badge variant="outline">{sourceLabel(e.source)}</Badge>
                    </td>
                    <td className="py-1 pr-3" data-testid="entry-detail-paid-by">
                      {native > 0
                        ? (e.party_name || detail.relationshipLabel || '--') + ' for ' + (e.counterparty_name || detail.counterpartyName || '--')
                        : native < 0
                          ? (e.counterparty_name || detail.counterpartyName || '--') + ' for ' + (e.party_name || detail.relationshipLabel || '--')
                          : '--'}
                    </td>
                    <td className={cn('py-1 pr-3 text-right font-mono whitespace-nowrap', native > 0 ? 'text-green-600' : native < 0 ? 'text-red-600' : '')}>
                      {formatSignedCurrency(native, e.currency)}
                    </td>
                    <td className="py-1 pr-3 text-right font-mono whitespace-nowrap">
                      {c.unconvertible ? (
                        <span className="text-muted-foreground">no rate</span>
                      ) : formatSignedCurrency(c.value || 0, displayCurrency)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// One relationship's (near-side party) monthly breakdown within a netted
// counterparty row. Rendered as a labeled sub-section: a subtotal header naming
// what distinguishes this relationship (party + currency mix, never the shared
// counterparty name), then the per-month movement / running-balance table whose
// most recent running balance equals this relationship's net.
function RelationshipBreakdown({ counterparty, counterpartyName, relationship, fxRates, displayCurrency, multi, onShowDetail }) {
  var breakdown = buildPositionBreakdown(relationship.entries, fxRates, displayCurrency);
  var subtotalClass = relationship.net > 0 ? 'text-green-600' : relationship.net < 0 ? 'text-red-600' : '';
  // Month-row test-ids stay backward-compatible for the common single-relationship
  // counterparty; only when a counterparty has multiple relationships do we
  // disambiguate the id/key by party (two relationships can share a month).
  var rowId = function (monthKey) {
    return multi
      ? 'breakdown-row-' + counterparty + '-' + relationship.party + '-' + monthKey
      : 'breakdown-row-' + counterparty + '-' + monthKey;
  };

  return (
    <div className="mb-3 last:mb-0" data-testid={'relationship-' + counterparty + '-' + relationship.party}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-medium">
          {relationship.relationshipLabel}
          {relationship.currencies && relationship.currencies.length > 0 ? (
            <span className="ml-2 text-muted-foreground font-normal">{relationship.currencies.join(', ')}</span>
          ) : null}
          {relationship.unconvertible ? (
            <span className="ml-2 text-muted-foreground font-normal">{relationship.unconvertibleCount + ' unconvertible'}</span>
          ) : null}
        </p>
        <span className={cn('text-xs font-mono font-medium', subtotalClass)}>
          {formatSignedCurrency(relationship.net, displayCurrency)}
        </span>
      </div>
      <div className="text-xs text-muted-foreground mb-1" data-testid={'relationship-owes-' + counterparty + '-' + relationship.party}>
        {owesSentence(relationship.relationshipLabel, counterpartyName, relationship.net, displayCurrency)}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b text-muted-foreground">
              <th className="text-left py-1 pr-3">Month</th>
              <th className="text-right py-1 pr-3">Entries</th>
              <th className="text-right py-1 pr-3">Movement</th>
              <th className="text-right py-1 pr-3">Running balance</th>
            </tr>
          </thead>
          <tbody>
            {breakdown.rows.map(function (r) {
              return (
                <tr
                  key={rowId(r.monthKey)}
                  className="border-b border-border/50 cursor-pointer hover:bg-muted/50"
                  data-testid={rowId(r.monthKey)}
                  title="Show the entries behind this figure"
                  onClick={function () {
                    onShowDetail({
                      counterpartyName: counterpartyName,
                      relationshipLabel: relationship.relationshipLabel,
                      label: r.label,
                      entries: r.entries,
                    });
                  }}
                >
                  <td className="py-1 pr-3 font-medium">{r.label}</td>
                  <td className="py-1 pr-3 text-right">{r.count}</td>
                  <td className={cn('py-1 pr-3 text-right font-mono', r.movement > 0 ? 'text-green-600' : r.movement < 0 ? 'text-red-600' : '')}>
                    {formatSignedCurrency(r.movement, displayCurrency)}
                  </td>
                  <td className={cn('py-1 pr-3 text-right font-mono', r.runningBalance > 0 ? 'text-green-600' : r.runningBalance < 0 ? 'text-red-600' : '')}>
                    {formatSignedCurrency(r.runningBalance, displayCurrency)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Positions tab: exactly ONE row per counterparty, netted across all
// relationships (near-side parties) and currencies in the display currency.
// The expansion gains a level: per-relationship subtotals, each followed by its
// own monthly breakdown. Parent net === sum(relationship nets) === last running
// balance of each relationship's breakdown (see buildNettedPositions), one path.
function PositionRow({ position, fxRates, displayCurrency, isExpanded, onToggle, onShowDetail }) {
  var dir = position.direction;
  var amountClass = position.net > 0 ? 'text-green-600' : position.net < 0 ? 'text-red-600' : '';
  var relationships = position.relationships || [];
  var multi = relationships.length > 1;

  return (
    <>
      <tr
        className="border-b cursor-pointer hover:bg-muted/50"
        onClick={onToggle}
        data-testid={'position-row-' + position.counterparty}
      >
        <td className="py-2 px-3">
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </td>
        <td className="py-2 px-3 font-medium">
          {position.counterparty_name}
          {multi ? (
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              {relationships.length + ' relationships'}
            </span>
          ) : null}
        </td>
        <td className="py-2 px-3">
          <Badge variant={dir === 'receivable' ? 'default' : dir === 'payable' ? 'destructive' : 'outline'}>
            {dir === 'receivable' ? 'Receivable' : dir === 'payable' ? 'Payable' : 'Settled'}
          </Badge>
          {position.unconvertible ? (
            <span className="ml-2 text-xs text-muted-foreground">{position.unconvertibleCount + ' unconvertible'}</span>
          ) : null}
        </td>
        <td className={cn('py-2 px-3 text-right font-mono', amountClass)}>
          {formatSignedCurrency(position.net, displayCurrency)}
        </td>
      </tr>
      {isExpanded && (
        <tr className="border-b bg-muted/20" data-testid={'breakdown-' + position.counterparty}>
          <td className="py-2 px-3"></td>
          <td className="py-2 px-3" colSpan={3}>
            <p className="text-xs font-medium text-muted-foreground mb-2">
              {multi ? 'Relationships and monthly breakdown' : 'Monthly breakdown'}
            </p>
            {relationships.map(function (rel) {
              return (
                <RelationshipBreakdown
                  key={rel.key}
                  counterparty={position.counterparty}
                  counterpartyName={position.counterparty_name}
                  relationship={rel}
                  fxRates={fxRates}
                  displayCurrency={displayCurrency}
                  multi={multi}
                  onShowDetail={onShowDetail}
                />
              );
            })}
          </td>
        </tr>
      )}
    </>
  );
}

// Positions tab: the positions table plus expandable per-month breakdowns.
// Shared across scopes; personal passes a settlement-control footer.
function PositionsTab({ entries, fxRates, displayCurrency, footer }) {
  var [expanded, setExpanded] = useState({});
  var [detail, setDetail] = useState(null);
  var positions = buildNettedPositions(entries, fxRates, displayCurrency);

  var toggle = function (key) {
    setExpanded(function (prev) {
      var next = Object.assign({}, prev);
      next[key] = !prev[key];
      return next;
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Intercompany Positions</CardTitle>
            <CardDescription>One row per counterparty, netted across all relationships and currencies (positive = receivable, negative = payable)</CardDescription>
          </div>
          <CurrencyToggle />
        </div>
      </CardHeader>
      <CardContent>
        {positions.length === 0 ? (
          <p className="text-muted-foreground text-sm">No intercompany positions found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="positions-table">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 w-8"></th>
                  <th className="text-left py-2 px-3">Counterparty</th>
                  <th className="text-left py-2 px-3">Direction</th>
                  <th className="text-right py-2 px-3">{'Net (' + displayCurrency + ')'}</th>
                </tr>
              </thead>
              <tbody>
                {positions.map(function (p) {
                  return (
                    <PositionRow
                      key={p.key}
                      position={p}
                      fxRates={fxRates}
                      displayCurrency={displayCurrency}
                      isExpanded={!!expanded[p.key]}
                      onToggle={function () { toggle(p.key); }}
                      onShowDetail={setDetail}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {footer ? <div className="mt-4">{footer}</div> : null}
        <EntryDetailDialog
          detail={detail}
          fxRates={fxRates}
          displayCurrency={displayCurrency}
          onClose={function () { setDetail(null); }}
        />
      </CardContent>
    </Card>
  );
}

// Entries tab: ledger contributions grouped by month (newest first), each month
// expandable to its individual entries, with counterparty/direction filters and
// a grand Total. Modeled on the portfolio Bookings tab. Month sums and the total
// derive from one filtered list (see buildEntriesView / the guard test), so they
// reconcile with the visible entries by construction.
function EntriesTab({ entries, fxRates, displayCurrency, showParty }) {
  var [selectedCounterparties, setSelectedCounterparties] = useState([]);
  var [directionFilter, setDirectionFilter] = useState('all');
  var [expandedMonths, setExpandedMonths] = useState({});

  var counterpartyOptions = ledgerCounterparties(entries);
  var nameById = {};
  counterpartyOptions.forEach(function (c) { nameById[c.id] = c.name; });

  var view = buildEntriesView(entries, fxRates, displayCurrency, {
    counterparties: selectedCounterparties,
    direction: directionFilter,
  });

  var toggleCounterparty = function (id) {
    setSelectedCounterparties(function (prev) {
      return prev.includes(id) ? prev.filter(function (v) { return v !== id; }) : prev.concat([id]);
    });
  };
  var clearAll = function () { setSelectedCounterparties([]); setDirectionFilter('all'); };
  var hasFilters = selectedCounterparties.length > 0 || directionFilter !== 'all';

  var toggleMonth = function (key) {
    setExpandedMonths(function (prev) {
      var next = Object.assign({}, prev);
      next[key] = !prev[key];
      return next;
    });
  };

  var amountClass = function (v) {
    return v > 0 ? 'text-green-600' : v < 0 ? 'text-red-600' : '';
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Intercompany Entries</CardTitle>
            <CardDescription>Individual movements grouped by month</CardDescription>
          </div>
          <CurrencyToggle />
        </div>
      </CardHeader>
      <CardContent>
        {/* Filters bar */}
        <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="entries-filters">
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <Filter className="h-4 w-4" /> Filters:
          </div>
          <select
            className="text-sm border rounded px-2 py-1 bg-background"
            value=""
            data-testid="counterparty-filter"
            onChange={function (e) { if (e.target.value) toggleCounterparty(e.target.value); }}
          >
            <option value="">{'Counterparty (' + (selectedCounterparties.length || 'All') + ')'}</option>
            {counterpartyOptions.map(function (c) {
              return (
                <option key={c.id} value={c.id}>
                  {(selectedCounterparties.includes(c.id) ? '✓ ' : '') + c.name}
                </option>
              );
            })}
          </select>
          <select
            className="text-sm border rounded px-2 py-1 bg-background"
            value={directionFilter}
            data-testid="direction-filter"
            onChange={function (e) { setDirectionFilter(e.target.value); }}
          >
            <option value="all">All directions</option>
            <option value="receivable">Receivable</option>
            <option value="payable">Payable</option>
          </select>
          {hasFilters && (
            <>
              {selectedCounterparties.map(function (id) {
                return (
                  <Badge key={id} variant="secondary" className="text-xs cursor-pointer" onClick={function () { toggleCounterparty(id); }}>
                    {(nameById[id] || id) + ' ×'}
                  </Badge>
                );
              })}
              {directionFilter !== 'all' && (
                <Badge variant="secondary" className="text-xs cursor-pointer capitalize" onClick={function () { setDirectionFilter('all'); }}>
                  {directionFilter + ' ×'}
                </Badge>
              )}
              <Button variant="ghost" size="sm" className="text-xs h-6" onClick={clearAll} data-testid="clear-filters">
                Clear all
              </Button>
            </>
          )}
          <span className="text-xs text-muted-foreground ml-auto" data-testid="entries-count">
            {view.total.count + ' ' + (view.total.count === 1 ? 'entry' : 'entries')}
          </span>
        </div>

        {view.total.count === 0 ? (
          <div className="text-center py-12">
            <ArrowRightLeft className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No intercompany entries</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="entries-table">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 w-8"></th>
                  <th className="text-left py-2 px-3">Date</th>
                  <th className="text-left py-2 px-3">Counterparty</th>
                  <th className="text-left py-2 px-3">Description</th>
                  <th className="text-left py-2 px-3">Direction</th>
                  <th className="text-right py-2 px-3">Native</th>
                  <th className="text-right py-2 px-3">{'Converted (' + displayCurrency + ')'}</th>
                </tr>
              </thead>
              <tbody>
                {view.groups.map(function (g) {
                  var isExpanded = expandedMonths[g.monthKey];
                  return (
                    <React.Fragment key={g.monthKey}>
                      <tr
                        className="border-b cursor-pointer hover:bg-muted/50 bg-muted/20"
                        onClick={function () { toggleMonth(g.monthKey); }}
                        data-testid={'month-row-' + g.monthKey}
                      >
                        <td className="py-2 px-3">
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </td>
                        <td className="py-2 px-3 font-medium" colSpan={4}>
                          {g.label}
                          <span className="ml-2 text-xs text-muted-foreground">
                            {g.count + ' ' + (g.count === 1 ? 'entry' : 'entries')}
                            {g.unconvertibleCount > 0 ? ', ' + g.unconvertibleCount + ' unconvertible' : ''}
                          </span>
                        </td>
                        <td className="py-2 px-3"></td>
                        <td className={cn('py-2 px-3 text-right font-mono font-medium', amountClass(g.sum))}>
                          {formatSignedCurrency(g.sum, displayCurrency)}
                        </td>
                      </tr>
                      {isExpanded && g.entries.map(function (e, idx) {
                        return (
                          <tr key={g.monthKey + '-' + (e.entry_id || idx)} className="border-b bg-background" data-testid="entry-row">
                            <td className="py-1 px-3"></td>
                            <td className="py-1 px-3 pl-8 text-muted-foreground text-xs">
                              {e.date ? format(new Date(e.date), 'dd MMM yyyy') : '-'}
                            </td>
                            <td className="py-1 px-3">
                              {showParty ? (
                                <span className="inline-flex items-center gap-1 text-xs">
                                  <span className="text-muted-foreground">{e.party_name || e.party}</span>
                                  <ArrowRightLeft className="h-3 w-3 text-muted-foreground" />
                                  <span>{e.counterparty_name || e.counterparty}</span>
                                </span>
                              ) : (e.counterparty_name || e.counterparty)}
                            </td>
                            <td className="py-1 px-3 max-w-[220px] truncate">
                              {e.description || '-'}
                              {e.source ? <span className="text-xs text-muted-foreground">{' · ' + sourceLabel(e.source)}</span> : null}
                            </td>
                            <td className="py-1 px-3">
                              <Badge variant={e.direction === 'receivable' ? 'default' : 'destructive'} className="text-xs">
                                {e.direction === 'receivable' ? 'Receivable' : 'Payable'}
                              </Badge>
                            </td>
                            <td className="py-1 px-3 text-right font-mono text-xs">
                              {formatSignedCurrency(e.signed_amount, e.currency)}
                            </td>
                            <td className={cn('py-1 px-3 text-right font-mono text-xs', amountClass(e.converted))}>
                              {e.unconvertible ? <span className="text-muted-foreground">unconvertible</span> : formatSignedCurrency(e.converted, displayCurrency)}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
                {/* Grand total over the filtered set */}
                <tr className="border-t-2 font-semibold bg-muted/30" data-testid="entries-total-row">
                  <td className="py-2 px-3"></td>
                  <td className="py-2 px-3" colSpan={4}>
                    {'Total (' + view.total.count + ' ' + (view.total.count === 1 ? 'entry' : 'entries') + ')'}
                    {view.total.unconvertibleCount > 0 ? (
                      <span className="ml-1 text-xs font-normal text-muted-foreground">
                        {view.total.unconvertibleCount + ' unconvertible excluded'}
                      </span>
                    ) : null}
                  </td>
                  <td className="py-2 px-3"></td>
                  <td className={cn('py-2 px-3 text-right font-mono', amountClass(view.total.sum))}>
                    {formatSignedCurrency(view.total.sum, displayCurrency)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Cost recharges tab: cash-settled reimbursements between entities (BI Ltd pays
// the Edinburgh landlords, ECI reimburses it monthly). These are deliberately
// NOT loan positions -- booking them as loans is what made ECI look like it owed
// BI Ltd GBP 208k while it was in fact paying the rent back every month (Fan,
// #finance-agent 2026-08-30). They stay fully visible here, per currency, with
// the composing bank movements.
function RechargesTab({ recharges, showParty }) {
  var [expanded, setExpanded] = useState({});
  var summary = (recharges && recharges.summary) || [];
  var entries = (recharges && recharges.entries) || [];

  var toggle = function (key) {
    setExpanded(function (prev) {
      var next = Object.assign({}, prev);
      next[key] = !prev[key];
      return next;
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cost recharges</CardTitle>
        <CardDescription>
          Costs one entity fronted and the other reimbursed in cash. Settled by the payment itself,
          so they are not loan balances — negative = paid out, positive = received.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {summary.length === 0 ? (
          <p className="text-muted-foreground text-sm">No cost recharges found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="recharges-table">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 w-8"></th>
                  {showParty && <th className="text-left py-2 px-3">Entity</th>}
                  <th className="text-left py-2 px-3">Counterparty</th>
                  <th className="text-left py-2 px-3">Currency</th>
                  <th className="text-right py-2 px-3">Total</th>
                </tr>
              </thead>
              <tbody>
                {summary.map(function (row) {
                  var key = row.party + '|' + row.counterparty + '|' + row.currency;
                  var isOpen = !!expanded[key];
                  var rowEntries = entries.filter(function (e) {
                    return e.party === row.party && e.counterparty === row.counterparty && e.currency === row.currency;
                  }).sort(function (a, b) { return String(b.date || '') < String(a.date || '') ? -1 : 1; });
                  return (
                    <React.Fragment key={key}>
                      <tr className="border-b hover:bg-muted/40 cursor-pointer" onClick={function () { toggle(key); }} data-testid={'recharge-row-' + key}>
                        <td className="py-2 px-3">
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </td>
                        {showParty && <td className="py-2 px-3">{row.party_name || row.party}</td>}
                        <td className="py-2 px-3 font-medium">{row.counterparty_name || row.counterparty}</td>
                        <td className="py-2 px-3">{row.currency}</td>
                        <td className="py-2 px-3 text-right font-mono">
                          {formatSignedCurrency(parseFloat(row.native_amount || '0'), row.currency)}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b bg-muted/20">
                          <td colSpan={showParty ? 5 : 4} className="py-2 px-3">
                            <table className="w-full text-xs">
                              <tbody>
                                {rowEntries.map(function (e, i) {
                                  return (
                                    <tr key={(e.entry_id || '') + i} className="border-b last:border-0">
                                      <td className="py-1 px-2 whitespace-nowrap">{e.date || '—'}</td>
                                      <td className="py-1 px-2">{e.description || '—'}</td>
                                      <td className="py-1 px-2">
                                        <Badge variant="outline">{sourceLabel(e.source)}</Badge>
                                      </td>
                                      <td className="py-1 px-2 text-right font-mono">
                                        {formatSignedCurrency(parseFloat(e.signed_amount || '0'), e.currency)}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function IntercompanyPage({ scope: scopeProp } = {}) {
  var isPersonal = scopeProp === 'personal';
  var location = useLocation();
  var [searchParams, setSearchParams] = useSearchParams();
  var storeState = useEntityStore();
  var selectedEntity = storeState.selectedEntity;
  var entities = storeState.entities;
  var displayCurrency = usePortfolioPrefsStore(function (s) { return s.displayCurrency; });
  var [ledgerEntries, setLedgerEntries] = useState([]);
  var [fxRates, setFxRates] = useState({});
  var [recharges, setRecharges] = useState({ summary: [], entries: [] });
  var [isLoading, setIsLoading] = useState(true);

  var activeTab = resolveIntercompanyTab(searchParams, location.pathname);

  var handleTabChange = function handleTabChange(tabKey) {
    var next = new URLSearchParams(searchParams);
    if (tabKey === 'positions') {
      next.delete('tab');
    } else {
      next.set('tab', tabKey);
    }
    setSearchParams(next);
  };

  // isAllEntities: entities scope with the global "All Entities" selection.
  var isAllEntities = !isPersonal && selectedEntity === 'all';

  var fetchData = async function fetchData() {
    try {
      // Both scopes now consume the single-source ledger. Personal -> personal
      // parties; entities single -> that entity; All Entities -> every entity.
      var ledgerParams = isPersonal
        ? { scope: 'personal', displayCurrency: displayCurrency }
        : isAllEntities
          ? { scope: 'entities', displayCurrency: displayCurrency }
          : { entityId: selectedEntity, displayCurrency: displayCurrency };
      var scopeParams = isPersonal
        ? { scope: 'personal' }
        : isAllEntities
          ? { scope: 'entities' }
          : { entityId: selectedEntity };
      var results = await Promise.all([
        intercompanyAPI.getLedger(ledgerParams),
        intercompanyAPI.getRecharges(scopeParams),
      ]);
      var ledgerRes = results[0];
      setLedgerEntries(ledgerRes.data.entries || []);
      setFxRates(ledgerRes.data.fx_rates || {});
      setRecharges(results[1].data || { summary: [], entries: [] });
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(function () {
    if (entities.length > 0 || selectedEntity === 'all' || isPersonal) {
      setIsLoading(true);
      fetchData();
    }
  }, [selectedEntity, entities, displayCurrency]);

  var handleSync = async function handleSync() {
    setIsLoading(true);
    try {
      var res = await intercompanyAPI.sync();
      var created = res.data.created;
      var updated = res.data.updated;
      await fetchData();
      toast.success('Intercompany synced: ' + created + ' new, ' + updated + ' updated');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  var Layout = isPersonal ? PersonalLayout : DashboardLayout;

  return (
    <Layout>
      <div className="flex flex-col gap-8" data-testid="intercompany-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Intercompany</h1>
            <p className="text-muted-foreground mt-1">Track balances between entities</p>
          </div>
          {!isPersonal && (
            <div className="flex gap-2">
              <Button onClick={handleSync} data-testid="sync-intercompany-btn">
                <RefreshCw className="h-4 w-4 mr-2" />
                Sync
              </Button>
            </div>
          )}
        </div>

        {/* Tab bar (both scopes share the standard segmented control) */}
        <IntercompanyTabBar activeTab={activeTab} onTabChange={handleTabChange} />

        {/* Loading spinner */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {/* Positions tab (both scopes) */}
        {!isLoading && activeTab === 'positions' && (
          <PositionsTab
            entries={ledgerEntries}
            fxRates={fxRates}
            displayCurrency={displayCurrency}
            footer={<IntercompanySettlementControl onApplied={fetchData} />}
          />
        )}

        {/* Entries tab (both scopes) */}
        {!isLoading && activeTab === 'entries' && (
          <EntriesTab
            entries={ledgerEntries}
            fxRates={fxRates}
            displayCurrency={displayCurrency}
            showParty={isAllEntities}
          />
        )}

        {/* Cost recharges tab (both scopes) */}
        {!isLoading && activeTab === 'recharges' && (
          <RechargesTab recharges={recharges} showParty={isAllEntities} />
        )}
      </div>
    </Layout>
  );
}
